import React, { FC, ReactElement, memo, useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { WarningPing } from 'shared/ui/WarningPing';
import { showNotification } from 'app/providers/NotificationsProvider';
import { ModernSelect as Select } from 'shared/ui/ModernSelect';
import { Form } from 'antd';
import { Lead, LeadLeaveReason, useUpdateLeadMutation } from 'entities/Lead';

interface LeftLeadModalProps {
  lead: Lead;
  action: ReactElement;
}

export const LeftLeadModal: FC<LeftLeadModalProps> = memo((props) => {
  const { lead, action } = props;

  const [isOpenedModal, setOpenedModal] = useState(false);

  const [form] = Form.useForm();

  const { t } = useAppTranslation(['user', 'common']);

  const [updateLead, { isLoading }] = useUpdateLeadMutation();

  const openModal = useCallback((): void => {
    setOpenedModal(true);
  }, []);

  const closeModal = useCallback((): void => {
    setOpenedModal(false);
  }, []);

  const leaveReasonOptions = Object.values(LeadLeaveReason).map((option) => ({
    label: t(option, { ns: 'common' }),
    value: option,
  }));

  const handleMarkAsLeftLead = useCallback(
    async ({ leaveReason }: Lead): Promise<void> => {
      try {
        if (lead && !lead.isLeft) {
          await updateLead({ leadId: lead.leadId, isLeft: !lead.isLeft, leaveReason }).unwrap();
        } else {
          await updateLead({ leadId: lead.leadId, isLeft: !lead.isLeft, leaveReason: null }).unwrap();
        }

        showNotification('info', t('Success', { ns: 'common' }), t('Lead left changed successfully', { ns: 'common' }));
      } catch (error: CustomAny) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when changing lead left', { ns: 'common' }));
      } finally {
        closeModal();
      }
    },
    [lead, updateLead, t, closeModal],
  );

  return (
    <>
      {React.cloneElement(action, { onClick: lead.isLeft ? handleMarkAsLeftLead : openModal })}

      <Modal className="max-w-[600px]" isOpen={isOpenedModal} onClose={closeModal}>
        <Form form={form} layout="vertical" onFinish={handleMarkAsLeftLead} className="flex flex-col">
          {/* <div className="mb-2">
            <WarningPing type="warning" />
          </div> */}
          <div className="font-semibold text-xl mb-3 font-tektur">{t('Mark lead as left')}</div>

          <div className="text-primaryLight font-normal">
            {t('Are you sure you want to mark this lead as left? You must select leave reason!')}
          </div>

          <div className="my-8">
            <Form.Item
              label={t('Select leave reason')}
              name="leaveReason"
              rules={[{ required: true, message: `${t('Please, select leave reason')}!` }]}
            >
              <Select options={leaveReasonOptions} placeholder={t('Select leave reason')} bordered />
            </Form.Item>
          </div>

          <div className="flex justify-end gap-3">
            <Button theme="secondary" onClick={closeModal}>
              {t('Cancel', { ns: 'common' })}
            </Button>

            <Button type="submit" isLoading={isLoading}>
              {t('Save', { ns: 'common' })}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
});
